import React from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, FONT_SIZES, TYPOGRAPHY, CONTENT_MAX_WIDTH } from '../../theme';
import { Header } from '@components/layout';

interface Section {
  title: string;
  body: string;
}

interface StaticContentScreenProps {
  title: string;
  sections: Section[];
  footerText?: string;
}

export function StaticContentScreen({ title, sections, footerText }: StaticContentScreenProps) {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();

  // ScrollView wraps the constrained content so the scrollbar lives at
  // the viewport edge (per the project convention of full-width
  // scrollbars on content screens), not at the 768px constraint edge.
  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <Header showBack title={title} />
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: insets.bottom + SPACING.xl },
        ]}>
        <View style={styles.constraint}>
          {sections.map((section, index) => (
            <View key={index} style={styles.section}>
              <Text style={[styles.sectionTitle, { color: colors.text }]}>{section.title}</Text>
              <Text style={[styles.sectionBody, { color: colors.textSecondary }]}>
                {section.body}
              </Text>
            </View>
          ))}

          {footerText && (
            <View style={[styles.footer, { borderTopColor: colors.divider }]}>
              <Text style={[styles.footerText, { color: colors.textTertiary }]}>{footerText}</Text>
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    alignItems: 'center',
    paddingTop: SPACING.md,
  },
  constraint: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    paddingHorizontal: SPACING.md,
  },
  section: {
    marginBottom: SPACING.lg,
  },
  sectionTitle: {
    ...TYPOGRAPHY.h4,
    fontSize: FONT_SIZES.md,
    marginBottom: SPACING.sm,
  },
  sectionBody: {
    ...TYPOGRAPHY.body,
    lineHeight: 21,
  },
  footer: {
    borderTopWidth: StyleSheet.hairlineWidth,
    marginTop: SPACING.lg,
    paddingTop: SPACING.md,
  },
  footerText: {
    ...TYPOGRAPHY.bodySmall,
    textAlign: 'center',
  },
});
